import { apiService } from '@shared/services/apiServices';
import technicianEndpoints from './technicianEndpoints';
import { AxiosError } from 'axios';
import { TECHNICIAN_ERROR_MESSAGES } from '../errors/errorMessages';

export interface UpdateTechnicianStatusResult {
  success: boolean;
  error?: string;
}

export interface UpdateTechnicianStatusParams {
  technicianId: string;
  isActive: boolean;
}

const DEFAULT_ERROR = 'Wystąpił błąd podczas aktualizacji statusu technika.';

const extractErrorMessage = (errorData: unknown): string => {
  if (!errorData) {
    return DEFAULT_ERROR;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData === 'object') {
    const apiError = errorData as { message?: string; error?: string };
    return apiError.message || apiError.error || DEFAULT_ERROR;
  }
  return DEFAULT_ERROR;
};

export const updateTechnicianStatusApiService = {
  updateTechnicianStatus: async (
    params: UpdateTechnicianStatusParams
  ): Promise<UpdateTechnicianStatusResult> => {
    try {
      const { technicianId, isActive } = params;

      const result = await apiService.patchAPI(
        `${technicianEndpoints.TECHNICIANS}/${technicianId}/status`,
        { isActive },
        { skipGlobalErrorRedirect: true }
      );

      if (!result.error || result.status === 204) {
        return { success: true };
      }

      const errorData = result.data;
      const statusCode = result.status;

      switch (statusCode) {
        case 403:
          return {
            success: false,
            error: TECHNICIAN_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
          };
        case 404:
          return {
            success: false,
            error: 'Technik nie został znaleziony.',
          };
        case 400:
          return {
            success: false,
            error: 'Proszę podać prawidłowe dane wejściowe.',
          };
        default:
          if (result.isNetworkError) {
            return {
              success: false,
              error: 'Błąd połączenia. Sprawdź połączenie internetowe.',
            };
          }
          return {
            success: false,
            error: extractErrorMessage(errorData),
          };
      }
    } catch (error) {
      if (!(error instanceof AxiosError)) {
        return {
          success: false,
          error: error instanceof Error ? error.message : DEFAULT_ERROR,
        };
      }

      const statusCode = error.response?.status;

      switch (statusCode) {
        case 404:
          return { success: false, error: 'Technik nie został znaleziony.' };
        case 400:
          return {
            success: false,
            error: 'Proszę podać prawidłowe dane wejściowe.',
          };
        default:
          if (statusCode === undefined && error.request) {
            return {
              success: false,
              error: 'Błąd połączenia. Sprawdź połączenie internetowe.',
            };
          }
          return {
            success: false,
            error: extractErrorMessage(error.response?.data),
          };
      }
    }
  },
};
